import type { StoreCarries, StoreSettings } from '@shared/types.ts';
import { getStore, useAppState } from '../../data/store.ts';
import { Icon } from '../../components/Icon.tsx';
import { back } from '../../lib/router.tsx';

function NumberField({ id, label, value, onChange, prefix, help }: { id: string; label: string; value: number; onChange: (n: number) => void; prefix?: string; help?: string }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="row" style={{ gap: 6 }}>
        {prefix && <span className="muted">{prefix}</span>}
        <input
          id={id}
          className="input"
          inputMode="decimal"
          defaultValue={String(value)}
          onBlur={(e) => {
            const n = Number(e.target.value.replace(/[$,]/g, ''));
            if (!Number.isNaN(n) && n >= 0 && n !== value) onChange(n);
            else e.target.value = String(value);
          }}
        />
      </div>
      {help && <span className="help">{help}</span>}
    </div>
  );
}

function StoreCard({ s }: { s: StoreSettings }) {
  const store = getStore();
  const update = (patch: Partial<StoreSettings>) => store.updateStore({ ...s, ...patch });
  return (
    <div className="card stack-lg">
      <div className="row-between">
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700 }}>{s.name}</span>
        <label className="row small" style={{ gap: 8 }}>
          <input type="checkbox" className="checkbox" checked={s.enabled} onChange={(e) => update({ enabled: e.target.checked })} />
          Use this store
        </label>
      </div>
      {s.enabled && (
        <>
          <div className="field">
            <label htmlFor={`${s.id}-carries`}>Takes</label>
            <select id={`${s.id}-carries`} className="select" value={s.carries ?? 'any'} onChange={(e) => update({ carries: e.target.value as StoreCarries })}>
              <option value="any">Everything</option>
              <option value="perishable">Perishables only</option>
              <option value="pantry">Pantry and household only</option>
            </select>
          </div>
          <div className="grid-2">
            <NumberField id={`${s.id}-min`} label="Minimum order" prefix="$" value={s.minimumOrder} onChange={(n) => update({ minimumOrder: n })} />
            <NumberField id={`${s.id}-free`} label="Free delivery from" prefix="$" value={s.freeDeliveryThreshold} onChange={(n) => update({ freeDeliveryThreshold: n })} />
          </div>
          <div className="grid-2">
            <NumberField id={`${s.id}-fee`} label="Fee below that" prefix="$" value={s.feeBelowThreshold} onChange={(n) => update({ feeBelowThreshold: n })} />
            <NumberField id={`${s.id}-hrs`} label="Delivery time (hours)" value={s.deliveryHours} onChange={(n) => update({ deliveryHours: n })} />
          </div>
          <div className="grid-2">
            <NumberField id={`${s.id}-pri`} label="Priority (1 = first)" value={s.priority} onChange={(n) => update({ priority: Math.max(1, Math.round(n)) })} help="Used when prices are unknown or tied" />
            <NumberField
              id={`${s.id}-cap`}
              label="Max items per order"
              value={s.maxUniqueItems ?? 0}
              onChange={(n) => update({ maxUniqueItems: n > 0 ? Math.round(n) : null })}
              help="0 = no limit"
            />
          </div>
        </>
      )}
    </div>
  );
}

export function StoresScreen() {
  const stores = useAppState((s) => s.stores);
  return (
    <div className="screen" style={{ gap: 14 }}>
      <button type="button" className="back-link" onClick={() => back('/settings')}>
        <Icon name="chevron-left" size={18} /> Settings
      </button>
      <h1 className="h1 h1-sm">Stores and minimums</h1>
      <span className="small muted">These rules decide which cart each item goes to: only stores that take that kind of item, then the lowest price per ounce, then the fastest, and only carts that clear their minimum.</span>
      {[...stores].sort((a, b) => a.priority - b.priority).map((s) => (
        <StoreCard key={s.id} s={s} />
      ))}
    </div>
  );
}
